import type { TFunction } from "i18next";

/**
 * Shared eFti minimal-field check.
 *
 * Used by both:
 *   - "Validate eFti" (edit mode, against live form values) — paths are fed
 *     to react-hook-form's setError so the offending inputs highlight.
 *   - "Send eFti" (readonly mode, against the saved shipment) — messages
 *     are rendered as a bulleted JSX list inside the toast.
 *
 * Returns { path, message } pairs so callers can pick whichever shape fits.
 */

export interface EftiFieldError {
  path: string;
  message: string;
}

const isValidObjectId = (id: string) => /^[a-f\d]{24}$/i.test(id);

const extractVehicleId = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && typeof value._id === "string") {
    return value._id;
  }
  return null;
};

const checkVehicleLicensePlate = (
  plateValue: any,
  type: "TRUCK" | "TRAILER",
  t: TFunction,
): string | null => {
  const id = extractVehicleId(plateValue);
  if (!id) return t(`formValidation.${type.toLowerCase()}Required`);
  if (!isValidObjectId(id)) {
    return t(`formValidation.${type.toLowerCase()}InvalidId`);
  }
  return null;
};

const checkPhone = (
  label: "pickup" | "delivery" | "carrier",
  phone: string | undefined,
  t: TFunction,
): string | null => {
  if (!phone || !phone.toString().trim()) {
    return t(`formValidation.${label}PhoneMissing`);
  }
  const digits = phone.toString().replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) {
    return t(`formValidation.${label}PhoneInvalid`);
  }
  return null;
};

const checkRegCode = (
  label: "pickup" | "delivery" | "carrier",
  rc: string | undefined,
  t: TFunction,
): string | null => {
  if (!rc || !rc.toString().trim()) {
    return t(`formValidation.${label}RegCodeMissing`);
  }
  return null;
};

export const validateEftiFields = (
  data: any,
  t: TFunction,
): EftiFieldError[] => {
  if (!data) {
    return [{ path: "", message: t("form.shipmentUndefined") }];
  }

  const errs: EftiFieldError[] = [];

  const push = (path: string, msg: string | null) => {
    if (msg) errs.push({ path, message: msg });
  };

  push(
    "pickup.registerationCode",
    checkRegCode("pickup", data.pickup?.registerationCode, t),
  );
  push(
    "pickup.phoneNumber",
    checkPhone("pickup", data.pickup?.phoneNumber, t),
  );
  push(
    "delivery.registerationCode",
    checkRegCode("delivery", data.delivery?.registerationCode, t),
  );
  push(
    "delivery.phoneNumber",
    checkPhone("delivery", data.delivery?.phoneNumber, t),
  );
  push(
    "carrier.registerationCode",
    checkRegCode("carrier", data.carrier?.registerationCode, t),
  );
  push(
    "carrier.phoneNumber",
    checkPhone("carrier", data.carrier?.phoneNumber, t),
  );

  push(
    "carrier.truckLicensePlate",
    checkVehicleLicensePlate(data.carrier?.truckLicensePlate, "TRUCK", t),
  );
  push(
    "carrier.trailerLicensePlate",
    checkVehicleLicensePlate(data.carrier?.trailerLicensePlate, "TRAILER", t),
  );

  if (!data.pickup?.date) {
    push("pickup.date", t("formValidation.pickupDateRequired"));
  }
  if (!data.pickup?.time) {
    push("pickup.time", t("formValidation.pickupTimeRequired"));
  }
  if (!data.delivery?.date) {
    push("delivery.date", t("formValidation.deliveryDateRequired"));
  }
  if (!data.delivery?.time) {
    push("delivery.time", t("formValidation.deliveryTimeRequired"));
  }

  return errs;
};
